/**
 * Maps a backend livestream status to a frontend broadcast state.
 *
 * Backend statuses (from Mux webhook → Laravel → Pusher):
 *   - 'active'          — encoder is streaming
 *   - 'connected'       — encoder reconnected within the reconnect window
 *   - 'disconnected'    — encoder stopped; 60s reconnect window (ignored — stay live)
 *   - 'idle'            — reconnect window expired OR stream was never started
 *   - 'recording_ready' — VOD recording is available
 *
 * Frontend broadcast states:
 *   - 'live'      — currently streaming (or within reconnect window)
 *   - 'ended'     — stream definitively ended (idle after live, or recording ready)
 *   - 'scheduled' — has broadcastId but never went live
 *   - 'idle'      — no stream configured
 */
export type BroadcastState = 'live' | 'ended' | 'scheduled' | 'idle'

export const mapStatusToBroadcastState = (
  backendStatus: string | undefined,
  wasLive: boolean,
  broadcastId: string | null,
  _playbackUrl: string | null
): BroadcastState => {
  // Active, connected, or disconnected — stream is live (or within reconnect window)
  if (
    backendStatus === 'active' ||
    backendStatus === 'connected' ||
    backendStatus === 'disconnected' ||
    backendStatus === 'recording'
  ) {
    return 'live'
  }

  // Recording ready — VOD available, stream is over
  if (backendStatus === 'recording_ready') {
    return 'ended'
  }

  // Idle or undefined — the definitive "it's over" signal
  if (wasLive) {
    return 'ended'
  }

  if (!broadcastId) {
    return 'idle'
  }

  return 'scheduled'
}
